import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { RiPencilLine, RiLoader4Line, RiCheckLine } from 'react-icons/ri';
import GlassCard from '../../components/ui/v2/GlassCard';
import { profileService } from '../../services/profileService';
import type { ProfileData, UserProfile } from '../../models/v2/profile';
import { notify } from '../../lib/notifications';
import { useProfile } from '../../context/ProfileContext';

export default function PersonalInfo() {
    const { profile: initialProfile } = useOutletContext<{ profile: UserProfile, fullData: ProfileData | null }>();

    // Local state for the profile to allow optimistic updates
    const [profile, setProfile] = useState<UserProfile>(initialProfile);

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Form State
    const [formData, setFormData] = useState<Partial<UserProfile>>({});

    // Sync formData when entering edit mode
    useEffect(() => {
        if (isEditing) {
            setFormData({
                first_name: profile.first_name || '',
                last_name: profile.last_name || '',
                preferred_name: profile.preferred_name || '',
                phone_number: profile.phone_number || '',
                headline: profile.headline || '',
                primary_field: profile.primary_field || '',
                location_city: profile.location_city || '',
                location_country: profile.location_country || '',
                postal_code: profile.postal_code || '',
                address_line_1: profile.address_line_1 || '',
                address_line_2: profile.address_line_2 || '',
                actively_looking: profile.actively_looking
            });
        }
    }, [isEditing, profile]);

    // Derived location string for display
    const locationDisplay = [profile.location_city, profile.location_country].filter(Boolean).join(', ') || profile.location || 'Not provided';

    const { updateLocalState } = useProfile();

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updated = await profileService.updatePersonalInfo(formData);
            setProfile(updated.profile);

            // Optimistic Update Global State
            updateLocalState(prev => {
                if (!prev) return prev;
                return { ...prev, profile: updated.profile };
            });

            setIsEditing(false);
            notify.success("Profile updated successfully");
        } catch (error) {
            console.error(error);
            notify.error("Failed to update profile");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const primaryFields = ["Engineering", "Product", "Data", "Design", "Business", "Other"];

    return (
        <div className="space-y-6">
            <GlassCard className="p-8 relative">
                {/* Header / Actions */}
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-white">Personal Info</h2>
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all border border-white/5"
                        >
                            <RiPencilLine size={20} />
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {/* LEFT COLUMN */}
                    <div className="space-y-8">
                        <Field
                            label="First Name"
                            value={profile.first_name}
                            isEditing={isEditing}
                            formValue={formData.first_name}
                            onChange={v => setFormData({ ...formData, first_name: v })}
                        />
                        <Field
                            label="Preferred Name"
                            value={profile.preferred_name}
                            isEditing={isEditing}
                            formValue={formData.preferred_name}
                            onChange={v => setFormData({ ...formData, preferred_name: v })}
                        />
                        <Field
                            label="Phone Number"
                            value={profile.phone_number}
                            isEditing={isEditing}
                            formValue={formData.phone_number}
                            onChange={v => setFormData({ ...formData, phone_number: v })}
                        />

                        {/* Location (City, Country combined) */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</label>
                            {isEditing ? (
                                <div className="flex gap-4">
                                    <input
                                        type="text"
                                        placeholder="City"
                                        value={formData.location_city || ''}
                                        onChange={e => setFormData({ ...formData, location_city: e.target.value })}
                                        className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-blue-500/50 outline-none"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Country"
                                        value={formData.location_country || ''}
                                        onChange={e => setFormData({ ...formData, location_country: e.target.value })}
                                        className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-blue-500/50 outline-none"
                                    />
                                </div>
                            ) : (
                                <div className="text-lg text-white font-medium">{locationDisplay}</div>
                            )}
                        </div>

                        <Field
                            label="Address 2"
                            value={profile.address_line_2}
                            isEditing={isEditing}
                            formValue={formData.address_line_2}
                            onChange={v => setFormData({ ...formData, address_line_2: v })}
                        />
                        <Field
                            label="Postal Code"
                            value={profile.postal_code}
                            isEditing={isEditing}
                            formValue={formData.postal_code}
                            onChange={v => setFormData({ ...formData, postal_code: v })}
                        />
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="space-y-8">
                        <Field
                            label="Last Name"
                            value={profile.last_name}
                            isEditing={isEditing}
                            formValue={formData.last_name}
                            onChange={v => setFormData({ ...formData, last_name: v })}
                        />
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email Address</label>
                            <div className="text-lg text-gray-400 font-medium select-none cursor-not-allowed opacity-80 decoration-slice">
                                <EmailField />
                            </div>
                        </div>

                        <Field
                            label="Headline"
                            value={profile.headline}
                            isEditing={isEditing}
                            formValue={formData.headline}
                            onChange={v => setFormData({ ...formData, headline: v })}
                        />

                        {/* Primary Field Select */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Primary Field</label>
                            {isEditing ? (
                                <select
                                    className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500/50 outline-none appearance-none"
                                    value={formData.primary_field || ''}
                                    onChange={e => setFormData({ ...formData, primary_field: e.target.value })}
                                >
                                    <option value="" disabled>Select Field</option>
                                    {primaryFields.map(f => (
                                        <option key={f} value={f} className="text-black">{f}</option>
                                    ))}
                                </select>
                            ) : (
                                <div className={`text-lg font-medium ${profile.primary_field ? 'text-white' : 'text-gray-500'}`}>
                                    {profile.primary_field || '-'}
                                </div>
                            )}
                        </div>

                        <Field
                            label="Address"
                            value={profile.address_line_1}
                            isEditing={isEditing}
                            formValue={formData.address_line_1}
                            onChange={v => setFormData({ ...formData, address_line_1: v })}
                        />
                    </div>
                </div>

                {/* Edit Actions */}
                {isEditing && (
                    <div className="flex justify-end gap-3 mt-12 pt-6 border-t border-white/5 animate-fade-in">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-6 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSaving ? <RiLoader4Line className="animate-spin" /> : <RiCheckLine />}
                            Save Changes
                        </button>
                    </div>
                )}
            </GlassCard>
        </div>
    );
}

// Helper Components

interface FieldProps {
    label: string;
    value?: string | null;
    isEditing: boolean;
    formValue?: string | null;
    onChange: (value: string) => void;
}

function Field({ label, value, isEditing, formValue, onChange }: FieldProps) {
    return (
        <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
            {isEditing ? (
                <input
                    type="text"
                    value={formValue || ''}
                    onChange={e => onChange(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-700"
                    placeholder={`Enter ${label}`}
                />
            ) : (
                <div className={`text-lg font-medium ${value ? 'text-white' : 'text-gray-500'}`}>
                    {value || '-'}
                </div>
            )}
        </div>
    );
}

import { useAuth } from '../../context/AuthContext';
function EmailField() {
    const { user } = useAuth();
    return (
        <div className="text-lg text-gray-500 font-medium select-none cursor-not-allowed opacity-60">
            {user?.email || 'Not available'}
        </div>
    );
}
